import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Eye, EyeOff, UploadCloud, CheckCircle2, Quote } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingScreen } from "@/components/common";

const toText = (v) => (Array.isArray(v) ? v.join("\n") : v || "");
const toList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function Field({ label, value, onChange, area, rows = 3, hint, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-navy-700">{label}</Label>
      {hint && <p className="text-[11px] text-navy-400 mb-1">{hint}</p>}
      {area ? (
        <Textarea className="mt-1" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <Input className="mt-1" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </div>
  );
}

export default function AdminAboutEdit() {
  const [doc, setDoc] = useState(null);
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/content/about").then((r) => { setDoc(r.data); setD(r.data.draft || r.data.published || {}); });
  useEffect(() => { load().catch(() => toast.error("Failed to load")); }, []);
  if (!d) return <DashboardShell menu={adminMenu} title="Edit About Us"><LoadingScreen /></DashboardShell>;

  const set = (k, v) => setD({ ...d, [k]: v });
  const saveDraft = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/content/about", d); setDoc(r.data); toast.success("Draft saved"); }
    catch { toast.error("Failed to save draft"); } finally { setSaving(false); }
  };
  const publish = async () => {
    setSaving(true);
    try { await api.put("/admin/content/about", d); const r = await api.post("/admin/content/about/publish"); setDoc(r.data); toast.success("Published — live on the website"); }
    catch { toast.error("Failed to publish"); } finally { setSaving(false); }
  };

  return (
    <DashboardShell menu={adminMenu} title="Edit About Us">
      <PageHeader title="Edit About Us" subtitle="Update the public About page. Save a draft, preview, then publish to go live."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="border-navy-300" onClick={() => setPreview(!preview)} data-testid="about-preview-toggle">
              {preview ? <><EyeOff size={16} className="mr-1.5" /> Edit</> : <><Eye size={16} className="mr-1.5" /> Preview</>}
            </Button>
            <Button variant="outline" className="border-navy-300" disabled={saving} onClick={saveDraft} data-testid="about-save-draft"><Save size={16} className="mr-1.5" /> Save Draft</Button>
            <Button className="bg-brand hover:bg-brand-600" disabled={saving} onClick={publish} data-testid="about-publish"><UploadCloud size={16} className="mr-1.5" /> Publish</Button>
          </div>
        } />
      {doc?.has_draft && !preview && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-2.5" data-testid="about-draft-badge">You have unpublished draft changes. Click Publish to make them live.</div>
      )}

      {preview ? (
        <div className="bg-white border border-navy-200 rounded-xl p-8 max-w-3xl space-y-6" data-testid="about-preview">
          <div><span className="brand-label text-brand">About</span><h1 className="mt-1 font-heading text-3xl font-extrabold text-navy-900">{d.heading}</h1><p className="mt-2 text-navy-600">{d.tagline}</p></div>
          {(d.intro || []).map((p, i) => <p key={i} className="text-navy-700 leading-relaxed">{p}</p>)}
          <div><h2 className="font-heading text-xl font-bold text-navy-900">Our Vision</h2><p className="mt-1 text-navy-600">{d.vision}</p></div>
          <div><h2 className="font-heading text-xl font-bold text-navy-900">Our Mission</h2><ul className="mt-2 space-y-1.5">{(d.mission || []).map((m, i) => <li key={i} className="flex gap-2 text-navy-700"><CheckCircle2 size={16} className="text-brand mt-0.5 shrink-0" />{m}</li>)}</ul></div>
          <div><h2 className="font-heading text-xl font-bold text-navy-900">{d.founder_name} — {d.founder_title}</h2><p className="mt-1 text-navy-500 text-sm">{d.founder_experience}</p>{(d.founder_bio || []).map((p, i) => <p key={i} className="mt-2 text-navy-700">{p}</p>)}<blockquote className="mt-3 border-l-4 border-brand bg-brand-50 rounded-r p-4 italic text-navy-800"><Quote size={16} className="text-brand mb-1" />“{d.founder_vision_statement}”</blockquote></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><h3 className="font-heading font-bold text-navy-900">For Customers</h3><ul className="mt-2 space-y-1">{(d.customer_benefits || []).map((b, i) => <li key={i} className="text-navy-700 text-sm">• {b}</li>)}</ul></div>
            <div><h3 className="font-heading font-bold text-navy-900">For Agencies</h3><ul className="mt-2 space-y-1">{(d.agency_benefits || []).map((b, i) => <li key={i} className="text-navy-700 text-sm">• {b}</li>)}</ul></div>
          </div>
          <p className="text-sm text-navy-500 border-t border-navy-200 pt-4">{d.verification_note}</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 max-w-6xl">
          <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4">
            <h3 className="font-heading font-semibold text-navy-900">Company Introduction</h3>
            <Field label="Heading" value={d.heading || ""} onChange={(v) => set("heading", v)} testid="f-heading" />
            <Field label="Tagline" value={d.tagline || ""} onChange={(v) => set("tagline", v)} area rows={2} testid="f-tagline" />
            <Field label="Introduction Paragraphs" hint="One paragraph per line" value={toText(d.intro)} onChange={(v) => set("intro", toList(v))} area rows={6} testid="f-intro" />
            <Field label="Vision" value={d.vision || ""} onChange={(v) => set("vision", v)} area rows={3} testid="f-vision" />
            <Field label="Mission Points" hint="One point per line" value={toText(d.mission)} onChange={(v) => set("mission", toList(v))} area rows={6} testid="f-mission" />
          </div>
          <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4">
            <h3 className="font-heading font-semibold text-navy-900">Founder</h3>
            <Field label="Founder Name" value={d.founder_name || ""} onChange={(v) => set("founder_name", v)} testid="f-founder-name" />
            <Field label="Founder Title" value={d.founder_title || ""} onChange={(v) => set("founder_title", v)} testid="f-founder-title" />
            <Field label="Founder Experience" value={d.founder_experience || ""} onChange={(v) => set("founder_experience", v)} area rows={2} testid="f-founder-exp" />
            <Field label="Founder Biography" hint="One paragraph per line" value={toText(d.founder_bio)} onChange={(v) => set("founder_bio", toList(v))} area rows={5} testid="f-founder-bio" />
            <Field label="Founder Vision Statement" value={d.founder_vision_statement || ""} onChange={(v) => set("founder_vision_statement", v)} area rows={3} testid="f-founder-vision" />
          </div>
          <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4">
            <h3 className="font-heading font-semibold text-navy-900">Benefits</h3>
            <Field label="Customer Benefits" hint="One per line" value={toText(d.customer_benefits)} onChange={(v) => set("customer_benefits", toList(v))} area rows={6} testid="f-cust-benefits" />
            <Field label="Agency Benefits" hint="One per line" value={toText(d.agency_benefits)} onChange={(v) => set("agency_benefits", toList(v))} area rows={6} testid="f-ag-benefits" />
          </div>
          <div className="bg-white border border-navy-200 rounded-xl p-6 space-y-4">
            <h3 className="font-heading font-semibold text-navy-900">Verification & CTA</h3>
            <Field label="Verification Note" value={d.verification_note || ""} onChange={(v) => set("verification_note", v)} area rows={3} testid="f-verif-note" />
            <Field label="CTA Heading" value={d.cta_heading || ""} onChange={(v) => set("cta_heading", v)} testid="f-cta-heading" />
            <Field label="CTA Text" value={d.cta_text || ""} onChange={(v) => set("cta_text", v)} area rows={2} testid="f-cta-text" />
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
